import { Page, Text, View, Document, StyleSheet, PDFViewer } from '@react-pdf/renderer';
import { formatDateLocale } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/currencyFormatter';

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 10
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5
  },
  subtitle: {
    fontSize: 12,
    color: '#6B7280'
  },
  section: {
    marginBottom: 15
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#111827'
  },
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: 15
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB'
  },
  tableColHeader: {
    width: '25%',
    padding: 5,
    backgroundColor: '#F3F4F6',
    fontWeight: 'bold',
    fontSize: 10
  },
  tableCol: {
    width: '25%',
    padding: 5,
    fontSize: 10
  },
  summary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB'
  },
  summaryItem: {
    width: '30%'
  },
  summaryTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5
  },
  summaryValue: {
    fontSize: 12
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    fontSize: 10,
    color: '#6B7280',
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 10
  }
});

// Create Document Component
const InvoicePDF = ({ transactions }) => {
  const totalAmount = transactions.reduce((sum, tx) => sum + tx.finalAmount, 0);
  const totalLateFees = transactions.reduce((sum, tx) => sum + (tx.lateFee || 0), 0);
  const completedCount = transactions.filter(tx => tx.status === 'completed').length;
  const pendingCount = transactions.filter(tx => tx.status === 'pending').length;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Transaction Report</Text>
          <Text style={styles.subtitle}>
            Generated on {formatDateLocale(new Date().toISOString())}
          </Text>
        </View>

        {/* Summary Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Summary</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View>
              <Text>Total Transactions: {transactions.length}</Text>
              <Text>Completed: {completedCount}</Text>
              <Text>Pending: {pendingCount}</Text>
            </View>
            <View>
              <Text>Total Amount: {formatCurrency(totalAmount)}</Text>
              <Text>Total Late Fees: {formatCurrency(totalLateFees)}</Text>
            </View>
          </View>
        </View>

        {/* Transactions Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Transaction Details</Text>
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View style={[styles.tableColHeader, { width: '20%' }]}>
                <Text>Invoice No</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '15%' }]}>
                <Text>Student</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '15%' }]}>
                <Text>Amount</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '15%' }]}>
                <Text>Payment Method</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '15%' }]}>
                <Text>Details</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '20%' }]}>
                <Text>Status</Text>
              </View>
            </View>

            {/* Table Rows */}
            {transactions.map((transaction, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.tableCol, { width: '20%' }]}>
                  <Text>{transaction.invoiceNo}</Text>
                  {transaction.installmentNumber && (
                    <Text style={{ fontSize: 8, color: '#6B7280', marginTop: 2 }}>
                      Installment #{transaction.installmentNumber}
                    </Text>
                  )}
                </View>
                <View style={[styles.tableCol, { width: '15%' }]}>
                  <Text>{transaction.studentName}</Text>
                  {transaction.scholarshipApplied && (
                    <Text style={{ fontSize: 8, color: '#9333EA', marginTop: 2 }}>
                      Scholarship Applied
                    </Text>
                  )}
                </View>
                <View style={[styles.tableCol, { width: '15%' }]}>
                  <Text>{formatCurrency(transaction.finalAmount)}</Text>
                  {transaction.baseAmount > transaction.finalAmount && (
                    <Text style={{ fontSize: 8, color: '#6B7280', textDecoration: 'line-through', marginTop: 2 }}>
                      {formatCurrency(transaction.baseAmount)}
                    </Text>
                  )}
                  {transaction.lateFee > 0 && (
                    <Text style={{ fontSize: 8, color: '#DC2626', marginTop: 2 }}>
                      +{formatCurrency(transaction.lateFee)} late fee
                    </Text>
                  )}
                  {transaction.refundedAmount > 0 && (
                    <Text style={{ fontSize: 8, color: '#DC2626', marginTop: 2 }}>
                      -{formatCurrency(transaction.refundedAmount)} refunded
                    </Text>
                  )}
                </View>
                <View style={[styles.tableCol, { width: '15%' }]}>
                  <Text>{transaction.paymentMethod || 'Other'}</Text>
                  {transaction.paymentMethodId && (
                    <Text style={{ fontSize: 8, color: '#6B7280', marginTop: 2 }}>
                      ID: {transaction.paymentMethodId}
                    </Text>
                  )}
                </View>
                <View style={[styles.tableCol, { width: '15%' }]}>
                  {transaction.discountBreakdown && transaction.discountBreakdown.length > 0 ? (
                    <View>
                      {transaction.discountBreakdown.map((item, idx) => (
                        <Text key={idx} style={{ fontSize: 8, marginTop: idx > 0 ? 2 : 0 }}>
                          {item.type}: {item.name} (-{formatCurrency(item.amount)})
                        </Text>
                      ))}
                    </View>
                  ) : (
                    <Text style={{ fontSize: 8, color: '#6B7280' }}>No discounts</Text>
                  )}
                  {transaction.refundedReason && (
                    <Text style={{ fontSize: 8, color: '#DC2626', marginTop: 2 }}>
                      Refund: {transaction.refundedReason}
                    </Text>
                  )}
                </View>
                <View style={[styles.tableCol, { width: '20%' }]}>
                  <Text>{transaction.status}</Text>
                  {transaction.notes && (
                    <Text style={{ fontSize: 8, color: '#6B7280', marginTop: 2 }}>
                      {transaction.notes}
                    </Text>
                  )}
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>This is an automatically generated report. For any questions, please contact support.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;